import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import {
  Plus, Search, Ticket, Calendar, Users, ChevronRight, Trash2,
  FileText, CheckCircle, Lock, XCircle, AlertTriangle,
} from 'lucide-react';

export const STATI_EVENTO = {
  BOZZA: { label: 'Bozza', classe: 'bg-gray-100 text-gray-700', icona: FileText },
  PUBBLICATO: { label: 'In vendita', classe: 'bg-green-100 text-green-700', icona: CheckCircle },
  CHIUSO: { label: 'Vendite chiuse', classe: 'bg-amber-100 text-amber-700', icona: Lock },
  ANNULLATO: { label: 'Annullato', classe: 'bg-red-100 text-red-700', icona: XCircle },
};

export const euro = (v) =>
  v === null || v === undefined || v === ''
    ? '—'
    : `€ ${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const dataIT = (v) =>
  v ? new Date(`${v}T12:00:00`).toLocaleDateString('it-IT') : '—';

export const dataOraIT = (v) =>
  v ? new Date(v).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '—';

/** L'ora dall'API arriva come 18:30:00, a video ne bastano cinque caratteri. */
export const ora = (v) => (v ? String(v).slice(0, 5) : '');

const Eventi = () => {
  const navigate = useNavigate();
  const { utente } = authStore();
  const modificabile = puoModificare(utente);

  const [eventi, setEventi] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [ricerca, setRicerca] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [mostraPassati, setMostraPassati] = useState(false);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      setEventi(await getAll('/navette/eventi/', mostraPassati ? { passati: 'si' } : {}));
    } catch (errore) {
      console.error('Errore caricamento eventi:', errore);
      notify.error('Errore nel caricamento degli eventi');
    } finally {
      setCaricamento(false);
    }
  }, [mostraPassati]);

  useEffect(() => { carica(); }, [carica]);

  const elimina = async (evento, e) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare "${evento.titolo}"?`)) return;
    const idAvviso = notify.loading('Eliminazione...');
    try {
      await api.delete(`/navette/eventi/${evento.id}/`);
      notify.update(idAvviso, { type: 'success', render: 'Evento eliminato' });
      carica();
    } catch (errore) {
      notify.update(idAvviso, {
        type: 'error',
        render: errore.response?.data?.errore || 'Errore nell\'eliminazione',
      });
    }
  };

  const testo = ricerca.trim().toLowerCase();
  const filtrati = eventi.filter((ev) => {
    if (filtroStato && ev.stato !== filtroStato) return false;
    if (!testo) return true;
    return [ev.titolo, ev.sottotitolo, ev.luogo]
      .some((v) => (v || '').toLowerCase().includes(testo));
  });

  const conta = (stato) => eventi.filter((ev) => ev.stato === stato).length;
  const postiInVendita = eventi
    .filter((ev) => ev.stato === 'PUBBLICATO')
    .reduce((somma, ev) => somma + (ev.posti_liberi || 0), 0);

  /* Un evento pubblicato senza piu' posti e' il caso che l'ufficio
     deve vedere subito: la vetrina lo mostra ancora, ma non vende. */
  const esauriti = eventi.filter(
    (ev) => ev.stato === 'PUBBLICATO' && ev.posti_totali > 0 && ev.posti_liberi === 0
  );

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Eventi</h1>
            <p className="text-sm text-gray-500 mt-0.5">Navette per concerti, mercatini, partite</p>
          </div>
          {modificabile && (
            <button
              onClick={() => navigate('/admin/eventi/nuovo')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium text-sm"
            >
              <Plus className="w-4 h-4" />
              Nuovo evento
            </button>
          )}
        </div>

        {esauriti.length > 0 && (
          <div className="flex items-start gap-2.5 p-3 mb-5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {esauriti.length === 1
                ? `"${esauriti[0].titolo}" è esaurito ma risulta ancora in vendita sul sito.`
                : `${esauriti.length} eventi sono esauriti ma risultano ancora in vendita sul sito.`}
              {' '}Chiudi le vendite o aggiungi una partenza.
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {['BOZZA', 'PUBBLICATO', 'CHIUSO'].map((stato) => {
            const Icona = STATI_EVENTO[stato].icona;
            return (
              <button
                key={stato}
                onClick={() => setFiltroStato(filtroStato === stato ? '' : stato)}
                className={`text-left bg-white rounded-xl border p-4 transition
                  ${filtroStato === stato ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200 hover:border-gray-400'}`}
              >
                <div className="flex items-center gap-2 text-gray-500 text-xs font-medium mb-1">
                  <Icona className="w-3.5 h-3.5" />
                  {STATI_EVENTO[stato].label}
                </div>
                <div className="text-2xl font-bold text-gray-900">{conta(stato)}</div>
              </button>
            );
          })}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs font-medium mb-1">
              <Users className="w-3.5 h-3.5" />
              Posti ancora in vendita
            </div>
            <div className="text-2xl font-bold text-gray-900">{postiInVendita}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              placeholder="Cerca per titolo o luogo"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-900"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={mostraPassati}
              onChange={(e) => setMostraPassati(e.target.checked)}
              className="w-4 h-4"
            />
            Mostra anche gli eventi passati
          </label>
        </div>

        {caricamento ? (
          <div className="py-16 text-center text-gray-400">Carico…</div>
        ) : filtrati.length === 0 ? (
          <div className="py-16 text-center">
            <Ticket className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {eventi.length === 0
                ? 'Nessun evento. Creane uno per iniziare a vendere posti.'
                : 'Nessun evento con questi filtri.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
            {filtrati.map((ev) => {
              const stato = STATI_EVENTO[ev.stato] || STATI_EVENTO.BOZZA;
              const esaurito = ev.posti_totali > 0 && ev.posti_liberi === 0;
              return (
                <div
                  key={ev.id}
                  onClick={() => navigate(`/admin/eventi/${ev.id}`)}
                  className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer transition"
                >
                  <div className="w-12 shrink-0 text-center">
                    <div className="text-lg font-bold text-gray-900 leading-none">
                      {new Date(`${ev.data}T12:00:00`).getDate()}
                    </div>
                    <div className="text-[11px] uppercase text-gray-500 mt-0.5">
                      {new Date(`${ev.data}T12:00:00`)
                        .toLocaleDateString('it-IT', { month: 'short' })
                        .replace('.', '')}
                    </div>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate">{ev.titolo}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${stato.classe}`}>
                        {stato.label}
                      </span>
                      {ev.stato === 'PUBBLICATO' && !ev.vendite_aperte && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">
                          Fuori finestra di vendita
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5 truncate">
                      {[ev.luogo, `${ev.navette_attive} partenz${ev.navette_attive === 1 ? 'a' : 'e'}`]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>

                  <div className="hidden sm:block text-right shrink-0">
                    <div className={`text-sm font-semibold ${esaurito ? 'text-red-600' : 'text-gray-900'}`}>
                      {esaurito ? 'Esaurito' : `${ev.posti_liberi} liberi`}
                    </div>
                    <div className="text-xs text-gray-500">
                      su {ev.posti_totali} · da {euro(ev.prezzo_minimo)}
                    </div>
                  </div>

                  {modificabile && (
                    <button
                      onClick={(e) => elimina(ev, e)}
                      className="p-2 text-gray-300 hover:text-red-600 transition shrink-0"
                      aria-label={`Elimina ${ev.titolo}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Eventi;
